import React from 'react';
import { Box, Typography, IconButton, Chip } from '@mui/material';
import {
  HourglassEmpty, Timer, Schedule, TouchApp,
  Inventory, FactCheck,
  QrCodeScanner, CameraAlt, Description, Search, Numbers,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { BuilderBlock } from '../types';

const ICONS: Record<string, React.ReactNode> = {
  HourglassEmpty: <HourglassEmpty />,
  Timer: <Timer />,
  Schedule: <Schedule />,
  TouchApp: <TouchApp />,
  Inventory: <Inventory />,
  FactCheck: <FactCheck />,
  QrCodeScanner: <QrCodeScanner />,
  CameraAlt: <CameraAlt />,
  Description: <Description />,
  Search: <Search />,
  Numbers: <Numbers />,
};

const CATEGORY_LABELS: Record<string, string> = {
  trigger: 'TRIGGER',
  mission: 'MISSION TYPE',
  step: 'STEP',
};

interface BlockCardProps {
  block: BuilderBlock;
  isSelected: boolean;
  onSelect: () => void;
  onRemove: () => void;
  sortable?: boolean;
}

export function BlockCard({ block, isSelected, onSelect, onRemove, sortable = true }: BlockCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id, disabled: !sortable });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onSelect}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: isSelected ? block.color : 'divider',
        bgcolor: isSelected ? `${block.color}10` : 'background.paper',
        cursor: 'grab',
        transition: 'all 0.15s',
        '&:hover': { borderColor: block.color, bgcolor: `${block.color}08` },
        '&:active': { cursor: 'grabbing' },
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1.5,
          bgcolor: `${block.color}18`,
          color: block.color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {ICONS[block.icon] || <Inventory />}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {block.label}
        </Typography>
        <Chip
          label={CATEGORY_LABELS[block.category]}
          size="small"
          sx={{
            height: 18,
            fontSize: 10,
            fontWeight: 700,
            bgcolor: `${block.color}18`,
            color: block.color,
          }}
        />
      </Box>
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); onRemove(); }}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
